<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import MetaAdsAPI from 'dashboard/api/metaAds';

const props = defineProps({
  fields: { type: Array, default: () => [] },
});
// Config de "Rastreamento & Integrações" do formulário. Persistido em colunas
// jsonb dedicadas do form (tracking_config, meta_capi, notify) + duas strings
// (outbound_webhook_url/secret). Mutação de objeto exige reatribuição pra
// disparar o v-model (defineModel).
const trackingConfig = defineModel('trackingConfig', {
  type: Object,
  default: () => ({}),
});
const metaCapi = defineModel('metaCapi', { type: Object, default: () => ({}) });
const notify = defineModel('notify', { type: Object, default: () => ({}) });
const webhookUrl = defineModel('webhookUrl', { type: String, default: '' });
const webhookSecret = defineModel('webhookSecret', {
  type: String,
  default: '',
});

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const metaTestState = ref('idle');
const metaTestMessage = ref('');

// Exemplo do placeholder do template. Fora do i18n de propósito: o vue-i18n
// trata {{var}} como placeholder aninhado e quebra a compilação da mensagem.
const templatePlaceholder =
  'Novo lead — {{form_name}}\nNome: {{name}}\nWhatsApp: {{phone}}\nutm_source: {{utm_source}}';
const whatsappInboxes = computed(
  () => getters['inboxes/getWhatsAppInboxes'].value
);

// Uma conta costuma ter mais de um pixel (um por empreendimento, por linha de
// produto). A lista vem da conta de anúncios conectada; vazia, o campo some e
// vale o pixel padrão da conta.
const pixels = ref([]);

const carregarPixels = async () => {
  try {
    const { data } = await MetaAdsAPI.datasets();
    pixels.value = data.datasets || [];
  } catch {
    pixels.value = [];
  }
};

onMounted(carregarPixels);

const setTracking = (key, value) => {
  trackingConfig.value = { ...trackingConfig.value, [key]: value };
};
const setMeta = (key, value) => {
  metaCapi.value = { ...metaCapi.value, [key]: value };
};
const setNotify = (key, value) => {
  notify.value = { ...notify.value, [key]: value };
};

const testMetaCapi = async () => {
  metaTestState.value = 'loading';
  metaTestMessage.value = '';
  try {
    const { data } = await MetaAdsAPI.sendTestEvent();
    metaTestState.value = 'success';
    metaTestMessage.value = t(
      'FORMS_MGMT.EDITOR.INTEGRATIONS.META.TEST_SUCCESS',
      { count: data.events_received || 1 }
    );
  } catch (error) {
    metaTestState.value = 'error';
    metaTestMessage.value =
      error.response?.data?.error ||
      t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.TEST_ERROR');
  }
};

// --- Aviso: vários destinatários (retrocompat com recipient único) ---
const recipients = computed(() => {
  const list = notify.value.recipients;
  if (Array.isArray(list) && list.length) return list;
  return [notify.value.recipient || ''];
});
const setRecipients = list => setNotify('recipients', list);
const addRecipient = () => setRecipients([...recipients.value, '']);
const removeRecipient = index =>
  setRecipients(recipients.value.filter((_, i) => i !== index));
const updateRecipient = (index, value) =>
  setRecipients(recipients.value.map((r, i) => (i === index ? value : r)));

// --- Mensagem personalizada: variáveis disponíveis + inserção ---
const availableVars = computed(() => {
  const std = [
    {
      key: 'form_name',
      label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_FORM'),
    },
    { key: 'name', label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_NAME') },
    {
      key: 'email',
      label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_EMAIL'),
    },
    {
      key: 'phone',
      label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_PHONE'),
    },
    {
      key: 'all_answers',
      label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_ALL_ANSWERS'),
    },
    {
      key: 'tracking',
      label: t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_TRACKING'),
    },
  ];
  const reserved = ['name', 'email', 'phone'];
  const fieldVars = (props.fields || [])
    .filter(f => f.key && !reserved.includes(f.key))
    .map(f => ({ key: f.key, label: f.label || f.key }));
  const utms = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ].map(k => ({ key: k, label: k }));
  return [...std, ...fieldVars, ...utms];
});
const insertVar = key => {
  const current = notify.value.message_template || '';
  setNotify('message_template', `${current}{{${key}}}`);
};

// --- Aviso por template (número oficial da Meta) ---
// Caixa oficial só entrega texto livre dentro da janela de 24h, que o número
// interno quase nunca tem aberta: com template aprovado o aviso sempre chega.
// Cada {{1}}/{{nome}} do corpo recebe uma variável do form (salva como token
// "{{name}}", resolvido no envio pelo Forms::SubmissionNotificationJob).
const OFFICIAL_PROVIDERS = ['default', 'whatsapp_cloud'];

const isOfficialNotifyInbox = computed(() => {
  const inbox = whatsappInboxes.value.find(
    i => String(i.id) === String(notify.value.whatsapp_inbox_id)
  );
  return OFFICIAL_PROVIDERS.includes(inbox?.provider);
});

const notifyTemplates = computed(() => {
  const id = notify.value.whatsapp_inbox_id;
  if (!id) return [];
  const list = getters['inboxes/getWhatsAppTemplates'].value(id) || [];
  return list.filter(tpl => String(tpl.status).toLowerCase() === 'approved');
});

const notifyTemplate = computed(() => {
  if (!isOfficialNotifyInbox.value || !notify.value.template_id) return null;
  return (
    notifyTemplates.value.find(
      tpl => String(tpl.id || tpl.name) === String(notify.value.template_id)
    ) || null
  );
});

const notifyTemplateBody = computed(
  () =>
    (notifyTemplate.value?.components || []).find(
      c => String(c.type).toUpperCase() === 'BODY'
    )?.text || ''
);

const notifyTemplateKeys = computed(() => [
  ...new Set(
    [...notifyTemplateBody.value.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(
      m => m[1]
    )
  ),
]);

// "{{1}}" montado no script: chaves duplas literais quebram o template do Vue.
const templateVarLabel = key => `{{${key}}}`;
const templateVarKey = key =>
  (notify.value.template_variables?.[key] || '').replace(/^\{\{|\}\}$/g, '');

const setTemplateVar = (key, varKey) => {
  setNotify('template_variables', {
    ...(notify.value.template_variables || {}),
    [key]: varKey ? `{{${varKey}}}` : '',
  });
};

const selectNotifyTemplate = value => {
  notify.value = {
    ...notify.value,
    template_id: value,
    template_variables: {},
  };
};

const isSyncingTemplates = ref(false);

// Puxa da Meta os templates aprovados da caixa. O sync roda em job: espera um
// pouco antes de recarregar as caixas (mesmo fluxo da nutrição por IA).
const syncNotifyTemplates = async () => {
  const inboxId = notify.value.whatsapp_inbox_id;
  if (!inboxId) return;
  isSyncingTemplates.value = true;
  try {
    await InboxesAPI.syncTemplates(inboxId);
    await new Promise(resolve => {
      setTimeout(resolve, 3000);
    });
    await store.dispatch('inboxes/get');
    useAlert(t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATES_SYNCED'));
  } catch {
    useAlert(t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATES_SYNC_ERROR'));
  } finally {
    isSyncingTemplates.value = false;
  }
};

const customParams = computed(() => trackingConfig.value.custom_params || []);
const addParam = () => {
  setTracking('custom_params', [...customParams.value, { key: '', label: '' }]);
};
const removeParam = index => {
  setTracking(
    'custom_params',
    customParams.value.filter((_, i) => i !== index)
  );
};
const updateParam = (index, field, value) => {
  const next = customParams.value.map((param, i) =>
    i === index ? { ...param, [field]: value } : param
  );
  setTracking('custom_params', next);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Rastreamento -->
    <section class="flex flex-col gap-3">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.HINT') }}
        </p>
      </div>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="trackingConfig.utm_enabled !== false"
          @change="setTracking('utm_enabled', $event.target.checked)"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.UTM') }}
        </span>
      </label>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="trackingConfig.capture_referrer !== false"
          @change="setTracking('capture_referrer', $event.target.checked)"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.REFERRER') }}
        </span>
      </label>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="trackingConfig.partial_capture_enabled !== false"
          @change="
            setTracking('partial_capture_enabled', $event.target.checked)
          "
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.PARTIAL_CAPTURE') }}
        </span>
      </label>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="trackingConfig.public_files_enabled === true"
          @change="setTracking('public_files_enabled', $event.target.checked)"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.PUBLIC_FILES') }}
        </span>
      </label>

      <div class="flex flex-col gap-2 rounded-xl border border-n-weak p-3">
        <div>
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.GOOGLE_TITLE') }}
          </h4>
          <p class="text-xs text-n-slate-10">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.GOOGLE_HINT') }}
          </p>
        </div>
        <input
          :value="trackingConfig.google_measurement_id"
          type="text"
          autocomplete="off"
          :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.GA4_PH')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm uppercase text-n-slate-12"
          @input="
            setTracking('google_measurement_id', $event.target.value.trim())
          "
        />
        <input
          :value="trackingConfig.google_tag_manager_id"
          type="text"
          autocomplete="off"
          :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.GTM_PH')"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm uppercase text-n-slate-12"
          @input="
            setTracking('google_tag_manager_id', $event.target.value.trim())
          "
        />
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.CUSTOM') }}
        </span>
        <div
          v-for="(param, index) in customParams"
          :key="index"
          class="flex items-center gap-2"
        >
          <input
            :value="param.key"
            type="text"
            :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.KEY_PH')"
            class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            @input="updateParam(index, 'key', $event.target.value)"
          />
          <input
            :value="param.label"
            type="text"
            :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.LABEL_PH')"
            class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            @input="updateParam(index, 'label', $event.target.value)"
          />
          <button
            type="button"
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
            @click="removeParam(index)"
          >
            <span class="i-lucide-trash-2 text-sm" />
          </button>
        </div>
        <button
          type="button"
          class="self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
          @click="addParam"
        >
          + {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.TRACKING.ADD') }}
        </button>
      </div>
    </section>

    <!-- Webhook de saída -->
    <section class="flex flex-col gap-3 border-t border-n-weak pt-6">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.WEBHOOK.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.WEBHOOK.HINT') }}
        </p>
      </div>
      <input
        v-model="webhookUrl"
        type="url"
        :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.WEBHOOK.URL_PH')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
      />
      <input
        v-model="webhookSecret"
        type="text"
        :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.WEBHOOK.SECRET_PH')"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
      />
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="trackingConfig.send_disqualified_webhook !== false"
          @change="
            setTracking('send_disqualified_webhook', $event.target.checked)
          "
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.WEBHOOK.SEND_DISQUALIFIED') }}
        </span>
      </label>
    </section>

    <!-- Aviso WhatsApp -->
    <section class="flex flex-col gap-3 border-t border-n-weak pt-6">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.HINT') }}
        </p>
      </div>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="notify.enabled === true"
          @change="setNotify('enabled', $event.target.checked)"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.ENABLE') }}
        </span>
      </label>
      <template v-if="notify.enabled">
        <select
          :value="notify.whatsapp_inbox_id"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          @change="setNotify('whatsapp_inbox_id', $event.target.value)"
        >
          <option value="">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.INBOX_PH') }}
          </option>
          <option
            v-for="inbox in whatsappInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
        <!-- Vários destinatários -->
        <div class="flex flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.RECIPIENTS_LABEL') }}
          </span>
          <div
            v-for="(rec, i) in recipients"
            :key="i"
            class="flex items-center gap-2"
          >
            <input
              :value="rec"
              type="text"
              :placeholder="t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.PHONE_PH')"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              @input="updateRecipient(i, $event.target.value)"
            />
            <button
              v-if="recipients.length > 1"
              type="button"
              class="shrink-0 rounded-md border border-n-weak p-1.5 text-n-slate-11 hover:bg-n-alpha-2"
              @click="removeRecipient(i)"
            >
              <span class="i-lucide-trash-2 block size-4" />
            </button>
          </div>
          <button
            type="button"
            class="self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
            @click="addRecipient"
          >
            + {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.ADD_RECIPIENT') }}
          </button>
        </div>

        <!-- Template (número oficial da Meta) -->
        <div v-if="isOfficialNotifyInbox" class="flex flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.OFFICIAL_TEMPLATE') }}
          </span>
          <p
            v-if="!notifyTemplate"
            class="rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
          >
            {{
              notifyTemplates.length
                ? t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.OFFICIAL_PICK')
                : t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.OFFICIAL_NONE')
            }}
          </p>
          <div class="flex items-center gap-2">
            <select
              :value="notifyTemplate ? notify.template_id : ''"
              data-testid="notify-template"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
              @change="selectNotifyTemplate($event.target.value)"
            >
              <option value="">
                {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATE_NONE') }}
              </option>
              <option
                v-for="tpl in notifyTemplates"
                :key="tpl.id || tpl.name"
                :value="tpl.id || tpl.name"
              >
                {{ tpl.name }}
              </option>
            </select>
            <button
              type="button"
              class="shrink-0 rounded-lg border border-n-weak px-3 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isSyncingTemplates"
              @click="syncNotifyTemplates"
            >
              {{
                isSyncingTemplates
                  ? t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATES_SYNCING')
                  : t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.SYNC_TEMPLATES')
              }}
            </button>
          </div>
          <template v-if="notifyTemplate">
            <p
              class="whitespace-pre-wrap rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
            >
              {{ notifyTemplateBody }}
            </p>
            <div
              v-for="key in notifyTemplateKeys"
              :key="key"
              class="flex items-center gap-2"
            >
              <span
                class="shrink-0 rounded-md bg-n-alpha-2 px-1.5 py-1 font-mono text-[11px] text-n-slate-11"
              >
                {{ templateVarLabel(key) }}
              </span>
              <span
                class="i-lucide-arrow-right size-3 shrink-0 text-n-slate-8"
              />
              <select
                :value="templateVarKey(key)"
                :data-testid="`notify-template-var-${key}`"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12"
                @change="setTemplateVar(key, $event.target.value)"
              >
                <option value="">
                  {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VAR_PICK') }}
                </option>
                <option v-for="v in availableVars" :key="v.key" :value="v.key">
                  {{ v.label }}
                </option>
              </select>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.VARS_HINT') }}
            </p>
          </template>
        </div>

        <!-- Mensagem personalizada (com template, a mensagem é o template) -->
        <div v-if="!notifyTemplate" class="flex flex-col gap-2">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATE_LABEL') }}
          </span>
          <p class="text-xs text-n-slate-10">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.NOTIFY.TEMPLATE_HINT') }}
          </p>
          <div class="flex flex-wrap gap-1">
            <button
              v-for="v in availableVars"
              :key="v.key"
              type="button"
              class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-[11px] text-n-slate-11 hover:bg-n-alpha-3"
              @click="insertVar(v.key)"
            >
              {{ v.label }}
            </button>
          </div>
          <textarea
            :value="notify.message_template"
            rows="6"
            :placeholder="templatePlaceholder"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            @input="setNotify('message_template', $event.target.value)"
          />
        </div>
      </template>
    </section>

    <!-- Meta Conversions API -->
    <section class="flex flex-col gap-3 border-t border-n-weak pt-6">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.HINT') }}
        </p>
      </div>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          class="h-4 w-4 accent-n-brand"
          :checked="metaCapi.enabled === true"
          @change="setMeta('enabled', $event.target.checked)"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.ENABLE') }}
        </span>
      </label>
      <label
        v-if="metaCapi.enabled && pixels.length"
        class="flex flex-col gap-1"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.PIXEL') }}
        </span>
        <select
          data-testid="seletor-pixel"
          :value="metaCapi.pixel_id || ''"
          class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          @change="setMeta('pixel_id', $event.target.value)"
        >
          <option value="">
            {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.PIXEL_DEFAULT') }}
          </option>
          <option v-for="pixel in pixels" :key="pixel.id" :value="pixel.id">
            {{ pixel.name }}
          </option>
        </select>
      </label>
      <select
        v-if="metaCapi.enabled"
        :value="metaCapi.event_name || 'Lead'"
        class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        @change="setMeta('event_name', $event.target.value)"
      >
        <option value="Lead">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.EVENTS.LEAD') }}
        </option>
        <option value="QualifiedLead">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.EVENTS.QUALIFIED_LEAD') }}
        </option>
        <option value="Proposal">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.EVENTS.PROPOSAL') }}
        </option>
        <option value="Purchase">
          {{ t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.EVENTS.PURCHASE') }}
        </option>
      </select>
      <template v-if="metaCapi.enabled">
        <button
          type="button"
          class="self-start rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="metaTestState === 'loading'"
          @click="testMetaCapi"
        >
          {{
            metaTestState === 'loading'
              ? t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.TESTING')
              : t('FORMS_MGMT.EDITOR.INTEGRATIONS.META.TEST')
          }}
        </button>
        <p
          v-if="metaTestMessage"
          class="rounded-lg px-3 py-2 text-xs"
          :class="
            metaTestState === 'success'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-ruby-3 text-n-ruby-11'
          "
        >
          {{ metaTestMessage }}
        </p>
      </template>
    </section>
  </div>
</template>
